import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getAccess, createAccess, deleteAccess } from "../../lib/api";
import type { AccessGrant } from "../../lib/types";

const PHONE_REGEX = /^\+\d{10,15}$/;

export function AccessTab() {
  const { t, managers } = useAuth();
  const [grants, setGrants] = useState<AccessGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("+380");
  const [managerId, setManagerId] = useState<string>(managers[0]?.id ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const { grants } = await getAccess();
      setGrants(grants);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);
  useEffect(() => { if (!managerId && managers[0]) setManagerId(managers[0].id); }, [managers, managerId]);

  async function submit() {
    setError(null);
    if (!PHONE_REGEX.test(phone)) { setError(t("access_form_invalid")); return; }
    if (!managerId) { setError("Select a manager"); return; }
    setSubmitting(true);
    try {
      await createAccess(phone, managerId);
      setPhone("+380");
      await reload();
    } catch (e: any) {
      if (e.message === "PHONE_ALREADY_BOUND") setError(t("access_form_dup"));
      else setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function revoke(g: AccessGrant) {
    if (!window.confirm(t("access_revoke_confirm", { phone: g.phone }))) return;
    try {
      await deleteAccess(g.id);
      await reload();
    } catch (e: any) {
      setError(e.message);
    }
  }

  const managerName = (id: string) => managers.find(m => m.id === id)?.full_name ?? id;

  return (
    <section className="p-4 space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-3">
        <div className="text-sm font-semibold text-slate-800">{t("access_add_title")}</div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">{t("access_form_phone")}</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+380XXXXXXXXX"
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">{t("access_form_manager")}</label>
          <select
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
          >
            {managers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </select>
        </div>
        {error && <div className="text-xs text-rose-600">{error}</div>}
        <button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition"
        >{t("access_form_submit")}</button>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="text-sm font-semibold text-slate-800 mb-3">{t("access_list_title")}</div>
        {loading && <div className="text-xs text-slate-500">…</div>}
        {!loading && grants.length === 0 && <div className="text-xs text-slate-500">{t("access_list_empty")}</div>}
        {grants.length > 0 && (
          <ul className="space-y-2">
            {grants.map(g => (
              <li key={g.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div>
                  <div className={`font-medium ${g.active ? "text-slate-900" : "text-slate-400 line-through"}`}>{g.phone}</div>
                  <div className="text-[11px] text-slate-500">→ {managerName(g.manager_id)} {!g.active && <span className="text-rose-500">{t("access_revoked_label")}</span>}</div>
                </div>
                {g.active === 1 && (
                  <button
                    onClick={() => revoke(g)}
                    className="text-rose-500 hover:text-rose-700 text-lg leading-none px-2"
                    aria-label="Revoke"
                  >×</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
